/**
 * Contextual help (FRD-CORE-041: "a responsive browser interface with contextual help").
 *
 * One entry per page, matched on the route. Each entry says only **what the page does**, **which
 * permissions it uses** and **which configuration it depends on** — facts of the system as built.
 * It states no business policy: who should hold a permission, which values to configure and what
 * a route should look like are TAQON decisions made in Administration, not help text.
 *
 * Permissions are the declared `<recordType>:<action>` names; the drawer shows which the current
 * session holds. Every page's API enforces its own permission regardless (SDD §22.4).
 */
export interface HelpPermission {
  readonly capability: string;
  readonly purpose: string;
}

export interface PageHelpEntry {
  /** A React Router pattern, e.g. `/hr/leave/requests/:id`. */
  readonly path: string;
  readonly title: string;
  readonly summary: string;
  readonly tasks?: readonly string[];
  readonly permissions: readonly HelpPermission[];
  readonly configuration?: readonly string[];
}

const SEQUENCE = (what: string) =>
  `A number sequence for ${what} (Administration › Number sequences). Without one, creating is refused.`;
const ROUTE = (what: string) =>
  `An approval route for ${what} in force (Administration › Approval routes). Without one, submission is refused.`;

const EXPORT_NOTE = 'Export (CSV) is offered only with the export permission and is audited.';

const LEAVE_CONFIG = [
  'Active leave types with their entitlement rules (HR › Leave types).',
  'The setting hr.leave_pro_rata_rounding, for leave types that accrue pro rata; balances fail closed without it.',
  ROUTE('leave requests'),
  SEQUENCE('leave requests'),
];

const EMPLOYEE_REQUEST_CONFIG = [
  'Request types in the list hr.employee_request_type (Administration › List values).',
  ROUTE('employee requests'),
  SEQUENCE('employee requests'),
];

const PURCHASE_REQUEST_CONFIG = [
  'Line categories in the list procurement.pr_category (Administration › List values).',
  'The setting commercial.rounding_mode for line amounts.',
  ROUTE('purchase requests'),
  SEQUENCE('purchase requests'),
];

const PURCHASE_ORDER_CONFIG = [
  'Suppliers (Procurement › Suppliers) and input tax codes (Finance › Tax configuration).',
  'The setting commercial.rounding_mode for line amounts.',
  ROUTE('purchase orders'),
  SEQUENCE('purchase orders'),
];

export const PAGE_HELP: readonly PageHelpEntry[] = [
  // Home, approvals, notifications, search
  {
    path: '/',
    title: 'Dashboard',
    summary:
      'A greeting, what is coming up at TAQON and an overview of what is recorded, read live on each visit. Coming up lists the events and public holidays of the next 30 days and the approved leave you may already see; once a day a popup lists the next 7 days. Each card appears only when you may view its records, and links to the dashboards you may open.',
    permissions: [
      {
        capability: 'hr.announcement:view',
        purpose: 'Events, news and public holidays under Coming up',
      },
      {
        capability: 'hr.leave_request:view',
        purpose: 'Approved leave under Coming up (your scope)',
      },
      { capability: 'platform.document:view', purpose: 'News pictures' },
      {
        capability: 'organization.department:view',
        purpose: 'Organization card (with the other organization views)',
      },
      { capability: 'ledger.account:view', purpose: 'Chart of accounts card' },
      { capability: 'procurement.supplier:view', purpose: 'Suppliers card' },
      { capability: 'platform.app_user:view', purpose: 'Users card' },
    ],
  },
  {
    path: '/dashboards/:code',
    title: 'Reporting dashboard',
    summary:
      'Figures for a period, each taken from the module that owns it and limited to the records you may see. A figure you may not see, or that the system cannot yet compute, is shown as not available with the reason, never as 0. "View records" opens the list behind a figure.',
    tasks: ['Choose the period (kept in the address).', 'Export the figures as CSV.'],
    permissions: [
      { capability: 'reporting.<dashboard>_dashboard:view', purpose: 'Open this dashboard' },
      { capability: 'reporting.<dashboard>_dashboard:export', purpose: 'Export it (audited)' },
      {
        capability: '<record type>:view',
        purpose: 'Each figure also needs view on its source records',
      },
    ],
    configuration: [
      'The setting reporting.<dashboard>_measures chooses which measures are shown; when it is not set, the dashboard’s standard measures are shown.',
    ],
  },
  {
    path: '/approvals',
    title: 'My approvals',
    summary:
      'Approval steps assigned to you, or to someone who delegated to you, what you decided, and your delegations. Only the users a step resolved to see it and can decide it.',
    tasks: [
      'Approve, reject or return a step.',
      'Delegate your approvals to another user for a period, and end a delegation.',
    ],
    permissions: [
      { capability: 'platform.approval_step:view', purpose: 'Open the page' },
      {
        capability: 'platform.approval_step:approve',
        purpose: 'Decide steps and manage delegations',
      },
      {
        capability: '<record type>:approve',
        purpose:
          'Each decision is re-checked against the record’s own approve permission and scope',
      },
    ],
    configuration: [
      'Approval routes in force (Administration › Approval routes) decide who each step goes to.',
      'Active segregation-of-duties rules (Administration › Segregation of duties) can block a decision.',
    ],
  },
  {
    path: '/notifications',
    title: 'Notifications',
    summary:
      'Your own in-system notifications, all or unread only, each linked to its record when you may open it. Sensitive values are always masked here.',
    permissions: [
      { capability: 'platform.notification:view', purpose: 'Read your own notifications' },
    ],
    configuration: [
      'Notification rules (Administration › Notification rules) decide which events notify whom.',
    ],
  },
  {
    path: '/search',
    title: 'Search',
    summary:
      'Finds records by code or name across the modules. Each module returns only the records you may view.',
    permissions: [{ capability: 'platform.search:view', purpose: 'Use search' }],
  },
  {
    path: '/documents/expiring',
    title: 'Expiring documents',
    summary:
      'Documents with a tracked expiry date that expire within the alert window, and those already expired, on records you may view.',
    permissions: [
      { capability: 'platform.document:view', purpose: 'Open the page' },
      { capability: '<record type>:view', purpose: 'See the owning record of each document' },
    ],
    configuration: [
      'The setting platform.document_expiry_alert_days sets the window.',
      'Document types that track expiry (Administration › Document types).',
    ],
  },

  // HR
  {
    path: '/hr/employees',
    title: 'Employees',
    summary: 'The employee master every module refers to, filtered and paged on the server.',
    tasks: ['Find employees.', 'Add an employee.', EXPORT_NOTE],
    permissions: [
      { capability: 'hr.employee:view', purpose: 'Open the list (within your scope)' },
      { capability: 'hr.employee:create', purpose: 'Add an employee' },
      { capability: 'hr.employee:export', purpose: 'Export' },
    ],
    configuration: [
      SEQUENCE('employees'),
      'The setting hr.employee_duplicate_match_rules, used to detect possible duplicates.',
      'Departments, cost centres, locations and positions to assign.',
    ],
  },
  {
    path: '/hr/employees/:id',
    title: 'Employee',
    summary:
      'One employee’s record, documents and history. Sensitive fields are masked unless a role grants their category unmasked.',
    tasks: [
      'Edit the record, including personal, home-country, bank and insurance details.',
      'Record education and certificates.',
      'Attach documents.',
      'Add or change the employee’s picture.',
      'Scan the business-card code to save the employee as a contact.',
    ],
    permissions: [
      { capability: 'hr.employee:view', purpose: 'Open the record (within your scope)' },
      {
        capability: 'hr.employee:change',
        purpose: 'Edit it, attach documents and set the picture',
      },
      { capability: 'hr.employee:view_unmasked', purpose: 'See designated sensitive values' },
    ],
    configuration: [
      'The setting hr.employee_picture_document_type, naming an image-only document type for employees; without it no picture is offered.',
      'Grades in the list hr.employee_grade (Administration › List values).',
    ],
  },
  {
    path: '/hr/my-record',
    title: 'My employee record',
    summary:
      'The employee record linked to your user. If your user is not linked to an employee, the page says so.',
    permissions: [{ capability: 'hr.employee:view', purpose: 'Open your record' }],
    configuration: ['Your user must be linked to an employee (Administration › Users).'],
  },
  {
    path: '/hr/positions',
    title: 'Positions',
    summary: 'The position titles used on employee records.',
    permissions: [
      { capability: 'hr.position:view', purpose: 'Open the list' },
      { capability: 'hr.position:create', purpose: 'Add a position' },
      { capability: 'hr.position:change', purpose: 'Edit or deactivate one' },
    ],
  },
  {
    path: '/hr/positions/:id',
    title: 'Position',
    summary: 'One position and the employees who hold it.',
    permissions: [
      { capability: 'hr.position:view', purpose: 'Open it' },
      { capability: 'hr.position:change', purpose: 'Edit it' },
      { capability: 'hr.employee:view', purpose: 'See its employees' },
    ],
  },
  {
    path: '/hr/announcements',
    title: 'Announcements',
    summary:
      'TAQON events and news. Active events of the next 30 days and current news appear on the dashboard of everyone who may see announcements, with the announced public holidays. Withdrawn announcements are kept, never deleted.',
    tasks: [
      'Add an event with its first and last day.',
      'Add news with an optional link to the article on another website.',
    ],
    permissions: [
      { capability: 'hr.announcement:view', purpose: 'Open the page' },
      { capability: 'hr.announcement:create', purpose: 'Add an announcement' },
    ],
  },
  {
    path: '/hr/announcements/:id',
    title: 'Announcement',
    summary:
      'One announcement. A picture attached here is the thumbnail of a news item on the dashboard; the newest picture that has passed the virus check is used.',
    tasks: ['Edit it.', 'Withdraw or restore it.', 'Attach a picture.'],
    permissions: [
      { capability: 'hr.announcement:view', purpose: 'Open it' },
      { capability: 'hr.announcement:change', purpose: 'Edit, withdraw or restore it' },
      { capability: 'platform.document:create', purpose: 'Attach a picture' },
    ],
    configuration: [
      'A document type for announcements that accepts JPEG, PNG or WebP pictures (Administration › Document types).',
    ],
  },
  {
    path: '/hr/leave',
    title: 'My leave',
    summary:
      'Your leave balances and requests. A balance is derived from entitlement, carry-forward and leave taken; pending requests are shown but not deducted. Short leave is asked for in hours on one date and has a monthly limit of hours instead of a balance. Creating a request submits it for approval.',
    tasks: [
      'Request leave.',
      'Request short leave in hours. Above the hours allowed in one request, the whole request is submitted as the leave type shown in the form instead.',
      'Cancel a request while it is submitted.',
      'Edit and resubmit a returned or rejected request.',
    ],
    permissions: [
      { capability: 'hr.leave_request:view', purpose: 'Open the page' },
      { capability: 'hr.leave_request:create', purpose: 'Request leave' },
    ],
    configuration: [
      'Your user must be linked to an employee (Administration › Users).',
      ...LEAVE_CONFIG,
    ],
  },
  {
    path: '/hr/leave/requests',
    title: 'Leave requests',
    summary: 'Leave across the employees you may see, for approvals and reporting.',
    tasks: ['Filter requests.', 'Raise a request on an employee’s behalf.', EXPORT_NOTE],
    permissions: [
      { capability: 'hr.leave_request:view', purpose: 'Open the list (within your scope)' },
      {
        capability: 'hr.leave_request:create',
        purpose: 'Raise a request (with hr.employee:view to pick the employee)',
      },
      { capability: 'hr.leave_request:export', purpose: 'Export' },
    ],
    configuration: LEAVE_CONFIG,
  },
  {
    path: '/hr/leave/requests/:id',
    title: 'Leave request',
    summary: 'One leave request, its approval progress and history.',
    permissions: [
      { capability: 'hr.leave_request:view', purpose: 'Open it' },
      {
        capability: 'hr.leave_request:change',
        purpose: 'Cancel it while submitted, or edit and resubmit',
      },
    ],
    configuration: [ROUTE('leave requests')],
  },
  {
    path: '/hr/leave-types',
    title: 'Leave types',
    summary:
      'The leave employees may request and the entitlement rule of each: entitlement days, leave year, accrual, probation, carry-forward cap and whether a negative balance is allowed. A type counted in hours is short leave: a monthly limit of hours, the most hours of one request, the hours of a working day and the leave type a longer request becomes. There are no built-in leave types.',
    permissions: [
      { capability: 'hr.leave_type:view', purpose: 'Open the list' },
      { capability: 'hr.leave_type:create', purpose: 'Add a leave type' },
      { capability: 'hr.leave_type:change', purpose: 'Edit or deactivate one' },
    ],
  },
  {
    path: '/hr/my-requests',
    title: 'My requests',
    summary:
      'HR requests raised for you, and where each one is. Creating a request submits it for approval.',
    tasks: [
      'Raise a request.',
      'Cancel one while it is submitted.',
      'Edit and resubmit a returned or rejected one.',
    ],
    permissions: [
      { capability: 'hr.employee_request:view', purpose: 'Open the page' },
      { capability: 'hr.employee_request:create', purpose: 'Raise a request' },
    ],
    configuration: [
      'Your user must be linked to an employee (Administration › Users).',
      ...EMPLOYEE_REQUEST_CONFIG,
    ],
  },
  {
    path: '/hr/requests',
    title: 'Employee requests',
    summary: 'HR requests across the employees you may see.',
    tasks: [
      'Filter by employee, department, type and status.',
      'Raise a request for an employee.',
      EXPORT_NOTE,
    ],
    permissions: [
      { capability: 'hr.employee_request:view', purpose: 'Open the list (within your scope)' },
      {
        capability: 'hr.employee_request:create',
        purpose: 'Raise a request (with hr.employee:view to pick the employee)',
      },
      { capability: 'hr.employee_request:export', purpose: 'Export' },
    ],
    configuration: EMPLOYEE_REQUEST_CONFIG,
  },
  {
    path: '/hr/requests/:id',
    title: 'Employee request',
    summary: 'One HR request, its approval progress and history.',
    permissions: [
      { capability: 'hr.employee_request:view', purpose: 'Open it' },
      {
        capability: 'hr.employee_request:change',
        purpose: 'Cancel it while submitted, or edit and resubmit',
      },
    ],
    configuration: [ROUTE('employee requests')],
  },

  // Procurement
  {
    path: '/suppliers',
    title: 'Suppliers',
    summary:
      'The supplier master. TRN and bank details are masked unless you are permitted to see them.',
    permissions: [
      { capability: 'procurement.supplier:view', purpose: 'Open the list' },
      { capability: 'procurement.supplier:create', purpose: 'Add a supplier' },
      { capability: 'procurement.supplier:change', purpose: 'Edit one' },
    ],
  },
  {
    path: '/suppliers/:id',
    title: 'Supplier',
    summary:
      'One supplier, its purchasing documents and, with Finance permissions, its invoices and payments.',
    permissions: [
      { capability: 'procurement.supplier:view', purpose: 'Open it' },
      { capability: 'procurement.supplier:change', purpose: 'Edit it' },
    ],
  },
  {
    path: '/procurement/purchase-requests',
    title: 'Purchase requests',
    summary: 'Purchase requests you may see, filtered and paged on the server.',
    tasks: ['Create a purchase request.', EXPORT_NOTE],
    permissions: [
      {
        capability: 'procurement.purchase_request:view',
        purpose: 'Open the list (within your scope)',
      },
      { capability: 'procurement.purchase_request:create', purpose: 'Create a request' },
      { capability: 'procurement.purchase_request:export', purpose: 'Export' },
    ],
    configuration: PURCHASE_REQUEST_CONFIG,
  },
  {
    path: '/procurement/my-purchase-requests',
    title: 'My purchase requests',
    summary: 'Your own purchase requests.',
    permissions: [
      { capability: 'procurement.purchase_request:view', purpose: 'Open the list' },
      { capability: 'procurement.purchase_request:create', purpose: 'Create a request' },
    ],
    configuration: PURCHASE_REQUEST_CONFIG,
  },
  {
    path: '/procurement/purchase-requests/new',
    title: 'New purchase request',
    summary:
      'Records what is needed, its estimated cost and where each line will be charged. It is saved as a draft until you submit it.',
    permissions: [
      { capability: 'procurement.purchase_request:create', purpose: 'Create a request' },
    ],
    configuration: PURCHASE_REQUEST_CONFIG,
  },
  {
    path: '/procurement/purchase-requests/:id/edit',
    title: 'Edit purchase request',
    summary: 'Changes a draft, returned or rejected request before it is submitted again.',
    permissions: [{ capability: 'procurement.purchase_request:change', purpose: 'Edit it' }],
  },
  {
    path: '/procurement/purchase-requests/:id',
    title: 'Purchase request',
    summary: 'One purchase request, its lines, approval progress and the orders raised from it.',
    tasks: [
      'Submit or resubmit it for approval.',
      'Cancel it.',
      'Create a purchase order once it is approved.',
    ],
    permissions: [
      { capability: 'procurement.purchase_request:view', purpose: 'Open it' },
      { capability: 'procurement.purchase_request:change', purpose: 'Edit, submit or cancel it' },
      { capability: 'procurement.purchase_order:create', purpose: 'Create an order from it' },
    ],
    configuration: [ROUTE('purchase requests')],
  },
  {
    path: '/procurement/purchase-orders',
    title: 'Purchase orders',
    summary: 'Orders from approved purchase requests, and the controlled emergency exception.',
    tasks: ['Create a purchase order.', EXPORT_NOTE],
    permissions: [
      {
        capability: 'procurement.purchase_order:view',
        purpose: 'Open the list (within your scope)',
      },
      { capability: 'procurement.purchase_order:create', purpose: 'Create an order' },
      { capability: 'procurement.purchase_order:export', purpose: 'Export' },
    ],
    configuration: PURCHASE_ORDER_CONFIG,
  },
  {
    path: '/procurement/purchase-orders/new',
    title: 'New purchase order',
    summary:
      'Creates an order for a supplier, from an approved purchase request or as an emergency exception. It is saved as a draft until you submit it.',
    permissions: [{ capability: 'procurement.purchase_order:create', purpose: 'Create an order' }],
    configuration: PURCHASE_ORDER_CONFIG,
  },
  {
    path: '/procurement/purchase-orders/:id/edit',
    title: 'Edit purchase order',
    summary: 'Changes a draft, returned or rejected order before it is submitted again.',
    permissions: [{ capability: 'procurement.purchase_order:change', purpose: 'Edit it' }],
  },
  {
    path: '/procurement/purchase-orders/:id',
    title: 'Purchase order',
    summary: 'One purchase order, its lines, approval progress and the receipts against it.',
    tasks: [
      'Submit it for approval, issue it once approved, close or cancel it.',
      'Record a receipt against an issued order.',
    ],
    permissions: [
      { capability: 'procurement.purchase_order:view', purpose: 'Open it' },
      {
        capability: 'procurement.purchase_order:change',
        purpose: 'Edit, submit, issue, close or cancel it',
      },
      { capability: 'procurement.goods_receipt:create', purpose: 'Record a receipt' },
    ],
    configuration: [ROUTE('purchase orders'), SEQUENCE('goods receipts (to record a receipt)')],
  },
  {
    path: '/procurement/receipts',
    title: 'Receipts',
    summary:
      'Goods and services received against issued purchase orders. Receipts are recorded from the order.',
    tasks: [EXPORT_NOTE],
    permissions: [
      { capability: 'procurement.goods_receipt:view', purpose: 'Open the list' },
      { capability: 'procurement.goods_receipt:export', purpose: 'Export' },
    ],
  },
  {
    path: '/procurement/receipts/:id',
    title: 'Receipt',
    summary: 'One receipt, its lines and documents.',
    permissions: [
      { capability: 'procurement.goods_receipt:view', purpose: 'Open it' },
      { capability: 'procurement.goods_receipt:create', purpose: 'Attach documents' },
    ],
  },

  // Administration
  {
    path: '/admin/users',
    title: 'Users',
    summary: 'People who can sign in. Access comes only from the roles assigned to them.',
    permissions: [
      { capability: 'platform.app_user:view', purpose: 'Open the list' },
      { capability: 'platform.app_user:create', purpose: 'Add a user' },
    ],
  },
  {
    path: '/admin/users/:id',
    title: 'User',
    summary:
      'One user: status, sign-in, linked employee and role assignments with their scope and dates.',
    tasks: [
      'Activate or deactivate the user.',
      'Issue a one-time sign-in setup link: the user chooses their own password and adds an authenticator app through it. The link is shown once.',
      'Reset a user’s sign-in: revokes their password, authenticator and recovery codes, signs them out, and issues a new link.',
      'Link an employee.',
      'Assign roles, optionally scoped.',
    ],
    permissions: [
      { capability: 'platform.app_user:view', purpose: 'Open it and see the sign-in state' },
      {
        capability: 'platform.app_user:change',
        purpose: 'Change status or linked employee; issue a setup link or reset sign-in',
      },
      { capability: 'platform.role_assignment:create', purpose: 'Assign a role' },
      { capability: 'platform.role_assignment:change', purpose: 'End an assignment' },
      { capability: 'hr.employee:view', purpose: 'Pick the employee to link' },
    ],
    configuration: [
      'The built-in sign-in policy: identity.password_min_length, identity.lockout_threshold, identity.lockout_minutes and identity.setup_link_validity_hours (Administration › Configuration). Without them, setup links and sign-in are refused.',
    ],
  },
  {
    path: '/admin/roles',
    title: 'Roles & permissions',
    summary:
      'Roles group permissions. A user’s access is the combination of the roles assigned to them. The system never checks a role’s name.',
    permissions: [
      { capability: 'platform.role:view', purpose: 'Open the list' },
      { capability: 'platform.role:create', purpose: 'Add a role' },
    ],
  },
  {
    path: '/admin/roles/:code',
    title: 'Role',
    summary:
      'One role, its permission grants and who holds it. Privileged permissions can be granted only to a privileged role.',
    permissions: [
      { capability: 'platform.role:view', purpose: 'Open it' },
      { capability: 'platform.role:change', purpose: 'Edit it' },
      { capability: 'platform.permission_grant:create', purpose: 'Grant a permission' },
      { capability: 'platform.permission_grant:change', purpose: 'Revoke a grant' },
    ],
  },
  {
    path: '/admin/configuration',
    title: 'Configuration',
    summary:
      'Policy values the system reads at run time. A change takes effect now or on a date, needs a reason, and is kept in history and the audit trail. Settings have no built-in defaults: a required setting that is not set makes its feature refuse until it is.',
    permissions: [
      { capability: 'platform.configuration_parameter:view', purpose: 'Open the page' },
      { capability: 'platform.configuration_parameter:change', purpose: 'Change a value' },
    ],
  },
  {
    path: '/admin/lists',
    title: 'List values',
    summary:
      'The choices offered in pick-lists. The code is permanent; values are deactivated, never deleted, and existing records keep theirs.',
    permissions: [
      { capability: 'platform.configurable_list_value:view', purpose: 'Open the page' },
      { capability: 'platform.configurable_list_value:create', purpose: 'Add a value' },
      {
        capability: 'platform.configurable_list_value:change',
        purpose: 'Edit or deactivate a value',
      },
    ],
  },
  {
    path: '/admin/number-sequences',
    title: 'Number sequences',
    summary: 'How business identifiers are formatted. A number once issued is never issued again.',
    permissions: [
      { capability: 'platform.number_sequence:view', purpose: 'Open the page' },
      { capability: 'platform.number_sequence:create', purpose: 'Add a sequence' },
      { capability: 'platform.number_sequence:change', purpose: 'Change a sequence' },
    ],
  },
  {
    path: '/admin/sensitive-fields',
    title: 'Sensitive fields',
    summary:
      'Fields that are masked wherever they appear — pages, exports, notifications — unless a role grants their category unmasked.',
    permissions: [
      { capability: 'platform.sensitive_field_designation:view', purpose: 'Open the page' },
      { capability: 'platform.sensitive_field_designation:create', purpose: 'Designate a field' },
      {
        capability: 'platform.sensitive_field_designation:change',
        purpose: 'Withdraw a designation',
      },
    ],
  },
  {
    path: '/admin/sod-rules',
    title: 'Segregation of duties',
    summary:
      'Rules that stop one user from performing conflicting actions on the same record. A blocked action is refused and recorded in the audit trail.',
    permissions: [
      { capability: 'platform.sod_rule:view', purpose: 'Open the page' },
      { capability: 'platform.sod_rule:create', purpose: 'Add a rule' },
      { capability: 'platform.sod_rule:change', purpose: 'Change or deactivate a rule' },
    ],
  },
  {
    path: '/admin/workflows',
    title: 'Approval routes',
    summary:
      'Who approves what, in which order. Each route is versioned; running approvals keep the version they started with. A record cannot be submitted until its route is in force.',
    tasks: ['Create a route or a new version.', 'Put a version in force, or take it out of force.'],
    permissions: [
      { capability: 'platform.workflow_definition:view', purpose: 'Open the page' },
      { capability: 'platform.workflow_definition:create', purpose: 'Create a route or version' },
      { capability: 'platform.workflow_definition:change', purpose: 'Put it in or out of force' },
    ],
    configuration: [
      'Approver roles must hold the record’s approve permission and platform.approval_step:view and :approve (Roles & permissions).',
      'Scoped role assignments, and reporting managers linked to users, for steps that resolve by department or manager.',
    ],
  },
  {
    path: '/admin/audit',
    title: 'Audit trail',
    summary:
      'Who did what, to which record, and when, searchable by record, actor, event, date and correlation ID. The trail cannot be changed by anyone.',
    permissions: [
      { capability: 'platform.audit_record:view', purpose: 'Open the page' },
      { capability: 'platform.app_user:view', purpose: 'Filter by user' },
    ],
  },
  {
    path: '/chat',
    title: 'Chat',
    summary:
      'Your one-to-one and group conversations. A one-to-one chat is visible only to its two participants; a group chat only to its members. New messages appear within about ten seconds while the page is open.',
    tasks: [
      'Start a one-to-one chat with any active user.',
      'Post, @mention a participant, and attach files to your own messages.',
      'Edit or delete your own messages. A deleted message is hidden from everyone.',
    ],
    permissions: [
      { capability: 'platform.conversation:view', purpose: 'Read your conversations' },
      {
        capability: 'platform.conversation:create',
        purpose: 'Start one-to-one chats, post and attach files',
      },
    ],
    configuration: [
      'The setting platform.chat_unread_email_delay_minutes, for the e-mail about an unread one-to-one message; without it none is sent.',
      'The setting platform.chat_attachment_document_type, naming a document type for Chat Message (Administration › Document types); without it attachments are refused.',
      'Notification rules (Administration › Notification rules) decide the chat notifications.',
    ],
  },
  {
    path: '/chat/:conversationId',
    title: 'Chat',
    summary:
      'One conversation. Only its participants can open it. Posting is closed in an archived group chat.',
    permissions: [
      { capability: 'platform.conversation:view', purpose: 'Read the conversation' },
      {
        capability: 'platform.conversation:create',
        purpose: 'Post, edit and delete your messages',
      },
    ],
  },
  {
    path: '/admin/chat-groups',
    title: 'Group chats',
    summary: 'Group chats and their members. One-to-one chats are never listed or reachable here.',
    tasks: ['Create a group chat and choose its members.'],
    permissions: [
      { capability: 'platform.chat_group:view', purpose: 'Open the page' },
      { capability: 'platform.chat_group:create', purpose: 'Create a group chat' },
    ],
  },
  {
    path: '/admin/chat-groups/:groupId',
    title: 'Group chat',
    summary:
      'One group chat: its members and its messages for moderation. A removed member no longer sees the group or its earlier messages; an archived group is read-only.',
    tasks: [
      'Rename or archive the group.',
      'Add or remove members.',
      'Edit or remove a message. The original text is kept in the audit trail and is not shown there.',
    ],
    permissions: [
      { capability: 'platform.chat_group:view', purpose: 'Open the group' },
      {
        capability: 'platform.chat_group:change',
        purpose: 'Rename, archive, change members and moderate messages',
      },
    ],
  },
  {
    path: '/admin/notification-rules',
    title: 'Notification rules',
    summary:
      'Who is notified of each event, on which channels and with which wording; and the delivery log of every notification, with its status and failure reason but never its content.',
    tasks: [
      'Add, edit, activate or deactivate a rule.',
      'Resend a failed e-mail from the delivery log; the resend is audited.',
    ],
    permissions: [
      {
        capability: 'platform.notification_rule:view',
        purpose: 'Open the page and the delivery log',
      },
      { capability: 'platform.notification_rule:create', purpose: 'Add a rule' },
      {
        capability: 'platform.notification_rule:change',
        purpose: 'Change a rule, or resend a failed e-mail',
      },
    ],
    configuration: [
      'E-mail is sent through the e-mail provider set in the deployment environment. Without one, every e-mail is marked failed with the reason “No email provider is configured”.',
    ],
  },
  {
    path: '/admin/document-types',
    title: 'Document types',
    summary:
      'The documents each kind of record may carry, whether they are confidential or track an expiry date, and which file types are accepted. Changes are audited.',
    permissions: [
      { capability: 'platform.document_type:view', purpose: 'Open the page' },
      { capability: 'platform.document_type:create', purpose: 'Add a type' },
      { capability: 'platform.document_type:change', purpose: 'Change or deactivate one' },
    ],
  },
  {
    path: '/admin/api-description',
    title: 'API description',
    summary:
      'Every operation of the TAQON Infinity API with the permission it requires, and the OpenAPI 3.1 document to download. It is generated by the API from its own routes, so it matches what is deployed. The API applies the same permissions, scope and masking as the application.',
    permissions: [{ capability: 'platform.api_description:view', purpose: 'Open the page' }],
  },
];
